import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiDownload,
  FiFileText,
  FiLoader,
  FiUploadCloud,
  FiXCircle,
} from 'react-icons/fi';
import api from '../services/api';
import useUser from '../hooks/useUser';

const TEMPLATE_COLUMNS = [
  { key: 'external_id', label: 'external_id' },
  { key: 'study_instance_uid', label: 'study_instance_uid' },
  { key: 'accession_number', label: 'accession_number' },
  { key: 'patient_id', label: 'patient_id' },
  { key: 'patient_name', label: 'patient_name' },
  { key: 'patient_birth_date', label: 'patient_birth_date' },
  { key: 'patient_sex', label: 'patient_sex' },
  { key: 'patient_age', label: 'patient_age' },
  { key: 'patient_phone', label: 'patient_phone' },
  { key: 'cccd', label: 'cccd' },
  { key: 'modality', label: 'modality' },
  { key: 'study_date', label: 'study_date' },
  { key: 'study_time', label: 'study_time' },
  { key: 'viewer_url', label: 'viewer_url' },
  { key: 'result_pdf_url', label: 'result_pdf_url' },
  { key: 'conclusion', label: 'conclusion' },
  { key: 'description', label: 'description' },
  { key: 'site_id', label: 'site_id' },
  { key: 'station_id', label: 'station_id' },
  { key: 'referring_physician_name', label: 'referring_physician_name' },
  { key: 'requesting_physician_name', label: 'requesting_physician_name' },
  { key: 'reason_for_study', label: 'reason_for_study' },
  { key: 'clinical_info', label: 'clinical_info' },
  { key: 'procedure_code', label: 'procedure_code' },
  { key: 'procedure_description', label: 'procedure_description' },
  { key: 'service_id', label: 'service_id' },
  { key: 'service_name', label: 'service_name' },
  { key: 'order_priority', label: 'order_priority' },
  { key: 'order_notes', label: 'order_notes' },
];

const SAMPLE_ROWS = [
  {
    external_id: 'PACS-RESULT-0001',
    study_instance_uid: '1.2.840.113619.2.55.3.604688654.123.20260512.1',
    accession_number: 'ACC202605120001',
    patient_id: 'BN000001',
    patient_name: 'Nguyen Van An',
    patient_birth_date: '1980-01-15',
    patient_sex: 'M',
    patient_age: '046Y',
    patient_phone: '0900000001',
    cccd: '001080000001',
    modality: 'CT',
    study_date: '20260512',
    study_time: '093000',
    viewer_url: 'https://viewer.example.com/studies/ACC202605120001',
    result_pdf_url: 'https://viewer.example.com/results/ACC202605120001.pdf',
    conclusion: 'Chua ghi nhan bat thuong cap tinh.',
    description: 'CT nguc',
    site_id: '',
    station_id: '',
    referring_physician_name: 'BS Nguyen Van Binh',
    requesting_physician_name: 'BS Tran Thi Hoa',
    reason_for_study: 'Kiem tra dinh ky',
    clinical_info: 'Thong tin lam sang tu PACS',
    procedure_code: 'CTCHEST',
    procedure_description: 'CT nguc co tiem thuoc',
    service_id: 'SVC001',
    service_name: 'Chan doan hinh anh',
    order_priority: 'normal',
    order_notes: '',
  },
];

const REQUIRED_FIELDS = ['patient_id', 'patient_name', 'modality', 'viewer_url'];
const IDENTIFIER_FIELDS = ['external_id', 'study_instance_uid', 'accession_number'];
const PREVIEW_COLUMNS = ['patient_id', 'patient_name', 'modality', 'viewer_url', 'external_id', 'study_instance_uid', 'accession_number'];
const IMPORT_CONCURRENCY = 4;

const loadExcelJS = async () => {
  const module = await import('exceljs');
  return module.default || module;
};

const normalizeCell = (value) => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'object') {
    if (typeof value.text === 'string') return value.text.trim();
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => part.text || '').join('').trim();
    }
    if (typeof value.result === 'string' || typeof value.result === 'number') {
      return String(value.result).trim();
    }
  }
  return String(value).trim();
};

const normalizeExcelCell = (cell) => {
  if (!cell) return '';
  const text = normalizeCell(cell.text);
  if (text) return text;
  return normalizeCell(cell.value);
};

const normalizeRow = (row) => {
  const normalized = {};
  Object.entries(row).forEach(([key, value]) => {
    const normalizedKey = String(key).trim();
    if (normalizedKey) {
      normalized[normalizedKey] = normalizeCell(value);
    }
  });
  return normalized;
};

const buildPayload = (row) => {
  const payload = {};
  TEMPLATE_COLUMNS.forEach(({ key }) => {
    const value = normalizeCell(row[key]);
    if (value) payload[key] = value;
  });
  return payload;
};

const validatePayload = (payload) => {
  const errors = [];

  REQUIRED_FIELDS.forEach((field) => {
    if (!payload[field]) errors.push(`Thiếu ${field}`);
  });

  const hasIdentifier = IDENTIFIER_FIELDS.some((field) => Boolean(payload[field]));
  if (!hasIdentifier) {
    errors.push('Cần ít nhất một mã: external_id, study_instance_uid hoặc accession_number');
  }

  if (payload.viewer_url) {
    try {
      const parsedUrl = new URL(payload.viewer_url);
      if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
        errors.push('viewer_url phải là URL http/https');
      }
    } catch {
      errors.push('viewer_url không hợp lệ');
    }
  }

  if (payload.result_pdf_url) {
    try {
      const parsedUrl = new URL(payload.result_pdf_url);
      if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
        errors.push('result_pdf_url pháº£i lÃ  URL http/https');
      }
    } catch {
      errors.push('result_pdf_url khÃ´ng há»£p lá»‡');
    }
  }

  if (payload.study_instance_uid && !/^[0-9.]+$/.test(payload.study_instance_uid)) {
    errors.push('study_instance_uid không đúng định dạng DICOM UID');
  }

  if (payload.study_date && !/^\d{8}$|^\d{4}-\d{2}-\d{2}$/.test(payload.study_date)) {
    errors.push('study_date phải là YYYYMMDD hoặc YYYY-MM-DD');
  }

  if (payload.patient_birth_date && !/^\d{8}$|^\d{4}-\d{2}-\d{2}$/.test(payload.patient_birth_date)) {
    errors.push('patient_birth_date phải là YYYYMMDD hoặc YYYY-MM-DD');
  }

  if (payload.study_time && !/^\d{6}(\.\d+)?$|^\d{2}:\d{2}:\d{2}$/.test(payload.study_time)) {
    errors.push('study_time phải là HHMMSS hoặc HH:MM:SS');
  }

  return errors;
};

const downloadTemplate = async () => {
  const ExcelJS = await loadExcelJS();
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('sync-result');
  worksheet.columns = TEMPLATE_COLUMNS.map((column) => ({
    header: column.label,
    key: column.key,
    width: Math.max(column.key.length + 2, 18),
  }));
  worksheet.getRow(1).font = { bold: true };
  SAMPLE_ROWS.forEach((row) => worksheet.addRow(row));

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'pacs-sync-result-template.xlsx';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const getErrorMessage = (error) => {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    'Import thất bại'
  );
};

const PacsResultImport = () => {
  const fileInputRef = useRef(null);
  const { user, role, siteId, canAccessAllSites } = useUser();
  const accountSiteId = siteId || (Array.isArray(user?.site_ids) ? user.site_ids[0] : '');
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [loadingSites, setLoadingSites] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ total: 0, done: 0, success: 0, failed: 0 });
  const canChooseSite = role === 'SuperAdmin' || canAccessAllSites;

  useEffect(() => {
    if (!canChooseSite) {
      setSelectedSiteId(accountSiteId || '');
      return;
    }

    let isMounted = true;
    const fetchSites = async () => {
      setLoadingSites(true);
      try {
        const response = await api.get('/orthanc/sites');
        const sitesData = response.data?.data || [];
        if (!isMounted) return;
        setSites(sitesData);
        setSelectedSiteId((current) => current || sitesData[0]?.id || '');
      } catch (error) {
        if (isMounted) {
          toast.error(getErrorMessage(error) || 'Không thể tải danh sách site');
        }
      } finally {
        if (isMounted) setLoadingSites(false);
      }
    };

    fetchSites();
    return () => {
      isMounted = false;
    };
  }, [accountSiteId, canChooseSite]);

  const summary = useMemo(() => {
    const valid = rows.filter((row) => row.errors.length === 0).length;
    const success = importProgress.success;
    const failed = importProgress.failed;
    return { total: rows.length, valid, invalid: rows.length - valid, success, failed };
  }, [importProgress.failed, importProgress.success, rows]);

  const parseExcelFile = async (file) => {
    setIsParsing(true);
    setFileName(file.name);
    setRows([]);
    setImportProgress({ total: 0, done: 0, success: 0, failed: 0 });

    try {
      const buffer = await file.arrayBuffer();
      const ExcelJS = await loadExcelJS();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);
      const worksheet = workbook.worksheets[0];
      const headerRow = worksheet?.getRow(1);
      const headers = [];

      headerRow?.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        headers[colNumber] = normalizeExcelCell(cell);
      });

      const jsonRows = [];
      worksheet?.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber === 1) return;

        const rowData = {};
        headers.forEach((header, colNumber) => {
          if (header) {
            rowData[header] = normalizeExcelCell(row.getCell(colNumber));
          }
        });
        jsonRows.push(rowData);
      });

      const parsedRows = jsonRows
        .map(normalizeRow)
        .filter((row) => Object.values(row).some((value) => normalizeCell(value)))
        .map((row, index) => {
          const payload = buildPayload(row);
          return {
            rowNumber: index + 2,
            payload,
            errors: validatePayload(payload),
            status: 'pending',
            message: '',
            responseData: null,
          };
        });

      setRows(parsedRows);
      if (parsedRows.length === 0) {
        toast.warn('File không có dữ liệu để import');
      } else {
        toast.success(`Đã đọc ${parsedRows.length} dòng từ Excel`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
      setFileName('');
      setImportProgress({ total: 0, done: 0, success: 0, failed: 0 });
    } finally {
      setIsParsing(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    parseExcelFile(file);
  };

  const clearFile = () => {
    setFileName('');
    setRows([]);
    setImportProgress({ total: 0, done: 0, success: 0, failed: 0 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const importRows = async () => {
    if (!selectedSiteId) {
      toast.warn('Vui lòng chọn site trước khi import');
      return;
    }

    const importableRows = rows.filter((row) => row.errors.length === 0);
    if (importableRows.length === 0) {
      toast.warn('Không có dòng hợp lệ để import');
      return;
    }

    setIsImporting(true);
    setImportProgress({ total: importableRows.length, done: 0, success: 0, failed: 0 });

    let successCount = 0;
    let failedCount = 0;
    const queue = [...importableRows];

    const runWorker = async () => {
      while (queue.length > 0) {
        const row = queue.shift();
        if (!row) return;

        try {
          await api.post('/pacs/sync-result', {
            ...row.payload,
            site_id: selectedSiteId,
          });
          successCount += 1;
          setImportProgress((current) => ({
            ...current,
            done: current.done + 1,
            success: current.success + 1,
          }));
        } catch (error) {
          failedCount += 1;
          setImportProgress((current) => ({
            ...current,
            done: current.done + 1,
            failed: current.failed + 1,
          }));
          console.error('Import PACS result failed:', error);
        }
      }
    };

    const workerCount = Math.min(IMPORT_CONCURRENCY, queue.length);
    await Promise.all(Array.from({ length: workerCount }, () => runWorker()));

    setIsImporting(false);
    if (failedCount > 0) {
      toast.error(`Import xong: ${successCount} thành công, ${failedCount} lỗi`);
    } else {
      toast.success(`Import thành công ${successCount} dòng`);
    }
  };

  const renderStatus = (row) => {
    if (row.errors.length > 0) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
          <FiAlertCircle /> Chưa hợp lệ
        </span>
      );
    }

    if (row.status === 'success') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
          <FiCheckCircle /> Thành công
        </span>
      );
    }

    if (row.status === 'failed') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700">
          <FiXCircle /> Lỗi
        </span>
      );
    }

    if (row.status === 'importing') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700">
          <FiLoader className="animate-spin" /> Đang import
        </span>
      );
    }

    return <span className="text-xs font-semibold text-slate-500">Sẵn sàng</span>;
  };

  const importCompletion = importProgress.total > 0 ? Math.round((importProgress.done / importProgress.total) * 100) : 0;

  return (
    <div className="h-full min-h-0 flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
        <div className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Import kết quả PACS</h1>
              <p className="text-sm text-slate-500">
                Import dữ liệu Excel vào API <span className="font-mono text-slate-700">/api/pacs/sync-result</span>.
              </p>
            </div>
            <button
              type="button"
              onClick={downloadTemplate}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <FiDownload /> Tải Excel mẫu
            </button>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(260px,420px)_1fr] md:items-end">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase text-slate-500">Site nhận dữ liệu</label>
                {canChooseSite ? (
                  <select
                    value={selectedSiteId}
                    onChange={(event) => setSelectedSiteId(event.target.value)}
                    disabled={loadingSites || isImporting}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-primary focus:outline-none disabled:bg-slate-100"
                  >
                    <option value="">{loadingSites ? 'Đang tải site...' : 'Chọn site'}</option>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name ? `${site.name} (${site.id})` : site.id}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={selectedSiteId || 'Site theo tài khoản API'}
                    readOnly
                    className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                  />
                )}
              </div>
              <div className="text-xs leading-5 text-slate-500">
                Site đã chọn sẽ được gửi trong <span className="font-mono text-slate-700">site_id</span> cho toàn bộ dòng import.
                Giá trị <span className="font-mono text-slate-700">site_id</span> trong Excel sẽ bị ghi đè để tránh import nhầm cơ sở.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-xs font-semibold uppercase text-slate-500">Tổng dòng</div>
              <div className="mt-1 text-2xl font-semibold text-slate-900">{summary.total}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-xs font-semibold uppercase text-slate-500">Hợp lệ</div>
              <div className="mt-1 text-2xl font-semibold text-emerald-700">{summary.valid}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-xs font-semibold uppercase text-slate-500">Chưa hợp lệ</div>
              <div className="mt-1 text-2xl font-semibold text-amber-700">{summary.invalid}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-xs font-semibold uppercase text-slate-500">Thành công</div>
              <div className="mt-1 text-2xl font-semibold text-emerald-700">{summary.success}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-xs font-semibold uppercase text-slate-500">Lỗi API</div>
              <div className="mt-1 text-2xl font-semibold text-red-700">{summary.failed}</div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <label className="flex min-h-[116px] flex-1 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center hover:bg-slate-100">
                <FiUploadCloud className="text-2xl text-slate-500" />
                <span className="mt-2 text-sm font-semibold text-slate-800">
                  {fileName || 'Chọn file Excel để import'}
                </span>
                <span className="mt-1 text-xs text-slate-500">Hỗ trợ .xlsx. Dòng đầu tiên là header.</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={handleFileChange}
                  disabled={isParsing || isImporting}
                />
              </label>

              <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
                <button
                  type="button"
                  onClick={importRows}
                  disabled={isParsing || isImporting || summary.valid === 0 || !selectedSiteId}
                  className="inline-flex min-w-[160px] items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {isImporting ? <FiLoader className="animate-spin" /> : <FiFileText />}
                  Import dữ liệu
                </button>
                <button
                  type="button"
                  onClick={clearFile}
                  disabled={isImporting || (!fileName && rows.length === 0)}
                  className="inline-flex min-w-[160px] items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
                >
                  <FiXCircle /> Xóa file
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-sm font-semibold text-slate-900">Tiến độ import</h2>
                <p className="text-xs text-slate-500">
                  Chỉ hiển thị tổng quan để giảm render và tăng tốc import.
                </p>
              </div>
              <div className="text-sm font-semibold text-slate-700">
                {isImporting ? `${importProgress.done}/${importProgress.total} dòng` : fileName ? 'Đã sẵn sàng import' : 'Chưa chọn file'}
              </div>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${importCompletion}%` }}
              />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-slate-600 sm:grid-cols-4">
              <div className="rounded-md bg-slate-50 px-3 py-2">Hợp lệ: <span className="font-semibold text-slate-900">{summary.valid}</span></div>
              <div className="rounded-md bg-slate-50 px-3 py-2">Lỗi dữ liệu: <span className="font-semibold text-slate-900">{summary.invalid}</span></div>
              <div className="rounded-md bg-slate-50 px-3 py-2">Thành công: <span className="font-semibold text-emerald-700">{summary.success}</span></div>
              <div className="rounded-md bg-slate-50 px-3 py-2">Thất bại: <span className="font-semibold text-red-700">{summary.failed}</span></div>
            </div>
          </div>

          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
            {rows.length === 0 ? (
              <p>Chưa có dữ liệu. Tải Excel mẫu, điền dữ liệu rồi chọn file để import.</p>
            ) : (
              <p>
                File đã được đọc xong. Khi import, hệ thống sẽ chạy song song nhiều request để rút ngắn thời gian xử lý.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PacsResultImport;
